/**
 * 営業ファネルダッシュボードの表示ロジック（GR-04c-4 / 沖中レポート工夫の UI 移植）。
 *
 * Slack 日報（adapter-salesforce/src/report/*）で確立された読み手向けの工夫を
 * 画面側でも同じ規約で再現する純関数群。対応関係:
 *  - 「-%」= 分母 0 を 0% と誤読させない（sales-daily-report.ts okPct と同一）
 *  - 前月比 "+N" / "-" = 「変化なし」と「未計測」の区別（funnel-a.ts delta と同一）
 *  - latestIsPartial = 当月（部分月）を確報と誤読させない（funnel-v2.ts buildHeadline）
 *  - 商材ファミリ化 = 枝番 "_NNN" を落として束ねる（funnel-segmented.ts familyOf）
 *  - 鮮度 48h fail-loud = segment snapshot 停止の可視化（daily-funnel-report-job.ts）
 *  - 月ラベル短縮 "4月" = スマホ折返し対策（funnel-segmented.ts shortMonthHeader）
 *
 * ここは表示計算のみ。データ取得（useFunnel）や描画（FunnelDashboard.tsx)を含めない。
 */
import type {
  FunnelMonthSlice,
  FunnelOverviewResponse,
  FunnelSegmentRow,
  FunnelStageCounts,
} from './api'

// ─── 数字の見せ方 ───

/** 率（小数 1 桁固定）。分母 0 / 非数は「-%」= 0% と誤読させない。 */
export function okPct(numerator: number, denominator: number): string {
  if (!Number.isFinite(denominator) || denominator <= 0) return '-%'
  return `${((numerator / denominator) * 100).toFixed(1)}%`
}

/** 前月比。+N / -N / ±0、比較先なし（undefined）は「-」で「変化なし」と区別する。 */
export function momDelta(current: number, previous: number | undefined): string {
  if (previous === undefined) return '-'
  const d = current - previous
  if (d > 0) return `+${d.toLocaleString()}`
  if (d < 0) return `${d.toLocaleString()}`
  return '±0'
}

/**
 * 桁区切り表示（表示層の責務。レポート側は生値だが画面は locale 整形してよい）。
 *
 * snapshot は指標が途中で増えることがある（実例: paid は 2026-05 開始のため
 * 2026-04 の stageCounts に paid キーが無い）。型は number 必須だが実データは
 * 欠けうるので、非数は「-」に落として画面ごと落とさない（fail-soft）。
 */
export function fmtCount(n: number | null | undefined): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return '-'
  return n.toLocaleString('ja-JP')
}

/** 月ラベル短縮 "2026-04" → "4月"（スマホ折返し対策・年は見出し側で補完）。 */
export function shortMonthLabel(period: string): string {
  const m = /^\d{4}-(\d{2})$/.exec(period)
  if (!m) return period
  return `${Number(m[1])}月`
}

// ─── ステージ定義（Slack レポート funnel-v2 と同一ラベル・同一順序） ───

export const STAGE_DEFS: ReadonlyArray<{ key: keyof FunnelStageCounts; label: string }> = [
  { key: 'juchu', label: '受注' },
  { key: 'application', label: '申込' },
  { key: 'entry', label: 'ET' },
  { key: 'construction', label: '工事予定' },
  { key: 'activated', label: '開通' },
  { key: 'cancelled', label: 'キャンセル' },
  { key: 'churned', label: '解約' },
  { key: 'paid', label: '入金' },
]

// ─── 部分月の扱い（latestIsPartial） ───

export interface ConfirmedAndPartial {
  /** 確報として扱う最終完了月（当月しか無い場合は null）。 */
  confirmed: FunnelMonthSlice | null
  /** 進行中の当月（📋速報）。 */
  partial: FunnelMonthSlice | null
  /** confirmed の前月（前月比の比較先）。 */
  previous: FunnelMonthSlice | null
}

/**
 * 月配列から「確報月 / 速報月 / 前月」を切り出す。
 * 最終月 = 進行中の当月とみなし、結論は最終完了月で語る
 * （部分月の開通 0 を異常と誤検知しない。funnel-v2 buildHeadline と同じ判断）。
 */
export function splitConfirmedAndPartial(
  months: readonly FunnelMonthSlice[],
): ConfirmedAndPartial {
  const withData = months.filter((m) => m.keiei !== null)
  const partial = withData.at(-1) ?? null
  const confirmed = withData.length >= 2 ? withData.at(-2)! : null
  const previous = withData.length >= 3 ? withData.at(-3)! : null
  return { confirmed, partial, previous }
}

// ─── 着地ペース換算（📈） ───

/**
 * 当月受注の単純ペース換算（受注 ÷ 経過日 × 月日数）。
 * 日報の「📈 着地見込」（予想着地時間×効率。目標ベースと現ペースの 2 本立て）とは
 * 別式のため、名前を変えて出す
 * （同名で別の数字を出すと役員が混乱する）。経過 3 日未満は精度が無いので null。
 */
export function paceProjection(
  partial: FunnelMonthSlice | null,
  now: Date,
): { projected: number; dayOfMonth: number; daysInMonth: number } | null {
  if (!partial?.keiei) return null
  const m = /^(\d{4})-(\d{2})$/.exec(partial.period)
  if (!m) return null
  const year = Number(m[1])
  const month = Number(m[2])
  // 表示月が現在月でないときはペース換算しない（過去月は確定値で語る）
  if (now.getFullYear() !== year || now.getMonth() + 1 !== month) return null
  const dayOfMonth = now.getDate()
  if (dayOfMonth < 3) return null
  const daysInMonth = new Date(year, month, 0).getDate()
  const projected = Math.round((partial.keiei.stageCounts.juchu / dayOfMonth) * daysInMonth)
  return { projected, dayOfMonth, daysInMonth }
}

// ─── 商材ファミリ化（funnel-segmented.ts familyOf と同一規約） ───

/** 商材名の枝番 "_NNN" を除去してファミリ名に丸める（"商材A_010" → "商材A"）。 */
export function familyOf(product: string): string {
  return product.replace(/_\d+$/, '')
}

export interface FamilyFunnelRow {
  family: string
  counts: FunnelStageCounts
}

function emptyCounts(): FunnelStageCounts {
  return {
    juchu: 0,
    application: 0,
    entry: 0,
    construction: 0,
    activated: 0,
    cancelled: 0,
    churned: 0,
    paid: 0,
  }
}

/** 欠落キー（指標が途中で増えた月）を 0 とみなす。素の加算だと NaN が表に出る。 */
function num(v: number | undefined): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0
}

function addCounts(a: FunnelStageCounts, b: FunnelStageCounts): FunnelStageCounts {
  return {
    juchu: num(a.juchu) + num(b.juchu),
    application: num(a.application) + num(b.application),
    entry: num(a.entry) + num(b.entry),
    construction: num(a.construction) + num(b.construction),
    activated: num(a.activated) + num(b.activated),
    cancelled: num(a.cancelled) + num(b.cancelled),
    churned: num(a.churned) + num(b.churned),
    paid: num(a.paid) + num(b.paid),
  }
}

/**
 * product 軸 segment 行（1 期間分）をファミリへ集約し、受注降順で返す。
 * topN を超えた分は「その他N商材」へ合算する（総数を欠落させない）。
 * 全指標 0 のファミリは出さない（ノイズ除去。データ源が動的なため
 * レポート側の「既定ファミリは 0 でも表示」は固定リストを持てない画面では適用しない）。
 */
export function aggregateFamilies(
  rows: readonly FunnelSegmentRow[],
  topN = 5,
): FamilyFunnelRow[] {
  const byFamily = new Map<string, FunnelStageCounts>()
  for (const row of rows) {
    if (row.product === null) continue
    const family = familyOf(row.product)
    byFamily.set(family, addCounts(byFamily.get(family) ?? emptyCounts(), row.stageCounts))
  }
  const all = [...byFamily.entries()]
    .map(([family, counts]) => ({ family, counts }))
    .filter((f) => Object.values(f.counts).some((v) => v > 0))
    .sort((a, b) => b.counts.juchu - a.counts.juchu)
  if (all.length <= topN) return all
  const top = all.slice(0, topN)
  const rest = all.slice(topN)
  const restCounts = rest.reduce((acc, f) => addCounts(acc, f.counts), emptyCounts())
  top.push({ family: `その他${rest.length}商材`, counts: restCounts })
  return top
}

// ─── ファネル 1 行表示（役員要望 2026-07-06 のフォーマットを画面へ） ───

export interface FunnelLineStep {
  label: string
  count: number
  /** 直前段からの到達率（先頭段は null）。分母 0 は "-%"。 */
  rate: string | null
}

/**
 * 受注→申込→ET→開通(→入金) の 1 行ファネル。
 * カッコ率は「受注比」（分母を揃えて縦比較できるようにする。分母は画面の note で明記）。
 */
export function buildFunnelLine(counts: FunnelStageCounts): FunnelLineStep[] {
  const base = counts.juchu
  return [
    { label: '受注', count: counts.juchu, rate: null },
    { label: '申込', count: counts.application, rate: okPct(counts.application, base) },
    { label: 'ET', count: counts.entry, rate: okPct(counts.entry, base) },
    { label: '開通', count: counts.activated, rate: okPct(counts.activated, base) },
    { label: '入金', count: counts.paid, rate: okPct(counts.paid, base) },
  ]
}

// ─── ハイライト（🏷 両極端の 2 点だけ） ───

/**
 * 当月 keiei の受注トップ 1 商材と離脱（キャンセル+解約）トップ 1 商材。
 * 全商材を並べず両極端だけを切り出す（buildFamilyHighlights と同じ思想）。
 */
export function buildTopHighlights(
  families: readonly FamilyFunnelRow[],
): { orderTop: FamilyFunnelRow | null; lossTop: FamilyFunnelRow | null } {
  if (families.length === 0) return { orderTop: null, lossTop: null }
  const real = families.filter((f) => !/^その他\d+商材$/.test(f.family))
  if (real.length === 0) return { orderTop: null, lossTop: null }
  const orderTop = [...real].sort((a, b) => b.counts.juchu - a.counts.juchu)[0] ?? null
  const lossTop =
    [...real].sort(
      (a, b) =>
        b.counts.cancelled + b.counts.churned - (a.counts.cancelled + a.counts.churned),
    )[0] ?? null
  return { orderTop, lossTop }
}

// ─── 鮮度 fail-loud（daily-funnel-report-job.ts と同じ 48h 規約） ───

export const STALE_THRESHOLD_HOURS = 48

/**
 * ⚠ アラート行。異常が無いときも「異常なし」を明示発話する
 * （無警告の沈黙 = 見た人が気づかない、を許さない）。
 * 鮮度不明（computedAt=null）は「判定なし」であり「異常なし」と言わない（三値区別）。
 */
export function buildAlertLine(computedAt: string | null, now: Date): {
  tone: 'ok' | 'warn' | 'unknown'
  text: string
} {
  if (computedAt === null) {
    return {
      tone: 'unknown',
      text: '⚠ 判定なし（最新月の集計 snapshot が揃っていません — snapshot job の稼働を確認してください）',
    }
  }
  const ageHours = (now.getTime() - new Date(computedAt).getTime()) / 3_600_000
  if (ageHours > STALE_THRESHOLD_HOURS) {
    return {
      tone: 'warn',
      text: `⚠ 集計が ${Math.floor(ageHours)}h 未更新（最終 ${computedAt.slice(0, 16).replace('T', ' ')}Z・しきい値 ${STALE_THRESHOLD_HOURS}h）— snapshot job の失敗を確認してください`,
    }
  }
  return { tone: 'ok', text: '⚠ アラート: 異常なし（集計は最新）' }
}

// ─── 月次テーブル（当月ベース / 受注月ベース） ───

export interface MonthlyStageTable {
  /** 列 = 月（古→新、短縮ラベル）。 */
  columns: string[]
  /** 行 = 8 指標固定順。cells は列順の件数（欠落月は null）。 */
  rows: Array<{ label: string; cells: Array<number | null>; delta: string }>
}

/**
 * 8 指標 × 月のテーブル。最右列の前月比（delta）を各行に付ける。
 * view: 'keiei' = 当月ベース（各イベント日が当月）/ 'bunseki' = 受注月ベース（cohort 到達）。
 */
export function buildMonthlyTable(
  overview: FunnelOverviewResponse,
  view: 'keiei' | 'bunseki',
): MonthlyStageTable {
  const columns = overview.months.map((m) => shortMonthLabel(m.period))
  const rows = STAGE_DEFS.map(({ key, label }) => {
    const cells = overview.months.map((m) => {
      const slice = view === 'keiei' ? m.keiei : m.bunseki
      if (!slice) return null
      // 指標が途中で増えた月は該当キーが無い（実例: paid は 2026-05 開始）。
      // 「欠落月は null」の契約に寄せて正規化する（undefined を漏らすと描画側の
      // null チェックをすり抜けて画面ごと落ちる）。
      const value = slice.stageCounts[key]
      return typeof value === 'number' && Number.isFinite(value) ? value : null
    })
    const last = cells.at(-1)
    const prev = cells.at(-2)
    const delta =
      last === null || last === undefined
        ? '-'
        : momDelta(last, prev === null || prev === undefined ? undefined : prev)
    return { label, cells, delta }
  })
  return { columns, rows }
}
